import { zodResolver } from '@hookform/resolvers/zod';
import { Archive, ArchiveRestore, FolderOpen, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useGroupsStore } from '@/features/groups/store';
import { useSettingsStore } from '@/features/settings/store';
import {
  Badge,
  Button,
  ColorPicker,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Money,
  PageHeader,
  SWATCH_COLORS,
  Select,
  confirm,
  toast,
  type Column,
} from '@/ui';
import { SUPPORTED_CURRENCIES, currencySymbol } from '@domain/money';
import type { Group } from '@domain/types';

const groupSchema = z.object({
  name: z.string().trim().min(1, 'Give the group a name.').max(60, 'Keep it under 60 characters.'),
  currency: z.string().min(1),
  color: z.string().min(1),
});

type GroupForm = z.infer<typeof groupSchema>;

export function GroupsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const groups = useGroupsStore((state) => state.groups);
  const summaries = useGroupsStore((state) => state.summaries);
  const activeGroupId = useGroupsStore((state) => state.activeGroupId);
  const setActiveGroup = useGroupsStore((state) => state.setActiveGroup);
  const createGroup = useGroupsStore((state) => state.create);
  const updateGroup = useGroupsStore((state) => state.update);
  const removeGroup = useGroupsStore((state) => state.remove);
  const defaultCurrency = useSettingsStore((state) => state.defaultCurrency);

  const [editing, setEditing] = useState<Group | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // The sidebar's "New group" item links here with ?new=1. Deriving the open
  // state from the URL avoids a setState-in-effect round trip.
  const requestedNew = searchParams.get('new') === '1';
  const isOpen = modalOpen || requestedNew;
  const target = requestedNew ? null : editing;

  function closeModal() {
    setModalOpen(false);
    if (requestedNew) setSearchParams({}, { replace: true });
  }

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  async function handleSubmit(values: GroupForm) {
    if (editing) {
      await updateGroup(editing.id, values);
      toast.success('Group updated.');
    } else {
      await createGroup(values);
      toast.success(`"${values.name}" created and opened.`);
    }
    closeModal();
  }

  async function handleDelete(group: Group) {
    const summary = summaries[group.id];
    const ok = await confirm({
      title: `Delete "${group.name}"?`,
      message:
        summary && summary.expenseCount > 0
          ? `This permanently removes ${summary.expenseCount} expense${
              summary.expenseCount === 1 ? '' : 's'
            } and ${summary.memberCount} member${
              summary.memberCount === 1 ? '' : 's'
            }. This cannot be undone.`
          : 'This cannot be undone.',
      confirmLabel: 'Delete group',
      destructive: true,
    });
    if (!ok) return;

    try {
      await removeGroup(group.id);
      toast.success('Group deleted.');
    } catch (error) {
      toast.error((error as Error).message);
    }
  }

  const columns: Column<Group>[] = [
    {
      key: 'name',
      header: 'Group',
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <span
            className="size-8 shrink-0 rounded-lg"
            style={{ backgroundColor: row.color }}
            aria-hidden
          />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate font-medium">{row.name}</span>
              {row.id === activeGroupId && <Badge tone="primary">Open</Badge>}
              {row.archived && <Badge>Archived</Badge>}
            </span>
            <span className="block text-[12px] text-muted">{row.currency}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => (
        <span className="tnum text-muted">{summaries[row.id]?.memberCount ?? 0}</span>
      ),
    },
    {
      key: 'expenses',
      header: 'Expenses',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => (
        <span className="tnum text-muted">{summaries[row.id]?.expenseCount ?? 0}</span>
      ),
    },
    {
      key: 'spent',
      header: 'Total spent',
      align: 'right',
      width: '150px',
      cell: (row) => (
        <Money minor={summaries[row.id]?.totalSpentMinor ?? 0} currency={row.currency} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '128px',
      cell: (row) => (
        <span className="flex justify-end gap-0.5" onClick={(event) => event.stopPropagation()}>
          <IconButton
            icon={Pencil}
            label={`Edit ${row.name}`}
            size="sm"
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
          />
          <IconButton
            icon={row.archived ? ArchiveRestore : Archive}
            label={row.archived ? `Unarchive ${row.name}` : `Archive ${row.name}`}
            size="sm"
            onClick={() => updateGroup(row.id, { archived: !row.archived })}
          />
          <IconButton
            icon={Trash2}
            label={`Delete ${row.name}`}
            size="sm"
            onClick={() => handleDelete(row)}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Groups"
        description="Each group keeps its own members, expenses and balances."
        actions={
          <Button icon={Plus} onClick={openCreate}>
            New group
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={groups}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          setActiveGroup(row.id);
          toast.info(`Opened "${row.name}".`);
        }}
        empty={
          <EmptyState
            icon={FolderOpen}
            title="No groups yet"
            description="A group is a set of people who share expenses — flatmates, a trip, a team."
            action={
              <Button icon={Plus} onClick={openCreate}>
                Create a group
              </Button>
            }
          />
        }
      />

      {groups.length > 0 && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-muted">
          <Users size={12} /> Click a row to open that group.
        </p>
      )}

      {isOpen && (
        <GroupModal
          key={target?.id ?? 'new'}
          onClose={closeModal}
          group={target}
          defaultCurrency={defaultCurrency}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}

function GroupModal({
  onClose,
  group,
  defaultCurrency,
  onSubmit,
}: {
  onClose: () => void;
  group: Group | null;
  defaultCurrency: string;
  onSubmit: (values: GroupForm) => Promise<void>;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GroupForm>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      name: group?.name ?? '',
      currency: group?.currency ?? defaultCurrency,
      color: group?.color ?? SWATCH_COLORS[0],
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={group ? 'Edit group' : 'New group'}
      width="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {group ? 'Save changes' : 'Create group'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              label="Group name"
              placeholder="Flat 4B, Goa trip, Friday lunch…"
              error={errors.name?.message}
              autoComplete="off"
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="currency"
          render={({ field }) => (
            <Select
              label="Currency"
              hint={group ? 'Changing this re-labels existing amounts; it does not convert them.' : undefined}
              options={SUPPORTED_CURRENCIES.map((code) => ({
                value: code,
                label: `${code} — ${currencySymbol(code)}`,
              }))}
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="color"
          render={({ field }) => (
            <ColorPicker label="Colour" value={field.value} onChange={field.onChange} />
          )}
        />
      </div>
    </Modal>
  );
}
